import { useNavigate } from 'react-router-dom';
import { Plus, ShoppingBag, Eye, CheckCircle, Send, Loader2 } from 'lucide-react';
import { useRequisicaoCompra } from '@/features/compras/hooks/useRequisicaoCompra';
import type { StatusRequisicaoCompra } from '@/features/compras/types';
import { PeriodoFiltro, usePeriodo } from '@/components/listas/PeriodoFiltro';
import { CabecalhoOrdenavel, useOrdenacao } from '@/components/listas/CabecalhoOrdenavel';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { BarraFiltros } from '@/components/BarraFiltros';
import { PaginacaoRodape } from '@/components/PaginacaoRodape';
import { UsuarioNome } from '@/components/UsuarioNome';
import { UsuarioSearchCombobox } from '@/components/UsuarioSearchCombobox';
import { useLocalEstoqueCombo } from '@/features/estoque/hooks/useLocalEstoque';
import { useFiltrosNaUrl } from '@/hooks/useFiltrosNaUrl';
import { usePaginacaoServidor } from '@/hooks/usePaginacaoServidor';

// USA-COM-11: solicitante, local, período de emissão e nº (Desktop FrmRequisicao).
// O período usa o PeriodoFiltro padrão das listas (estado local, vai ao servidor como dtInicio/dtFim).
const PADROES = {
  busca: '',
  status: 'todos',
  solicitanteId: 0,
  localEstoqueId: 0,
  pagina: 1,
  tamanhoPagina: 20,
};

const statusConfig: Record<StatusRequisicaoCompra, { label: string; className: string }> = {
  Rascunho:   { label: 'Rascunho',    className: 'bg-gray-500/10 text-gray-600'   },
  Pendente:   { label: 'Pendente',    className: 'bg-yellow-500/10 text-yellow-600' },
  EmAprovacao:{ label: 'Em Aprovação',className: 'bg-blue-500/10 text-blue-600'   },
  Aprovada:   { label: 'Aprovada',    className: 'bg-green-500/10 text-green-600'  },
  Rejeitada:  { label: 'Rejeitada',   className: 'bg-red-500/10 text-red-600'     },
  Cancelada:  { label: 'Cancelada',   className: 'bg-red-500/10 text-red-600'     },
  Atendida:   { label: 'Atendida',    className: 'bg-purple-500/10 text-purple-600' },
};

const RequisicaoCompra = () => {
  const navigate = useNavigate();
  const { filtros, setFiltros, limparFiltros, filtrosAtivos } = useFiltrosNaUrl(PADROES);
  const pag = usePaginacaoServidor({
    controlado: { pagina: filtros.pagina, tamanhoPagina: filtros.tamanhoPagina, onChange: setFiltros },
  });
  // Período e ordem vão ao servidor (padrão de listas da v118): a lista cresce sem limite com o tempo.
  const voltarPagina1 = () => setFiltros({ pagina: 1 });
  const periodo   = usePeriodo('30dias', voltarPagina1);
  const ordenacao = useOrdenacao<'emissao' | 'numero' | 'valor'>('emissao', true, voltarPagina1);
  const { data: locais = [] } = useLocalEstoqueCombo();
  const nomeLocal = (id?: number) => (id ? locais.find(l => l.id === id)?.descricao ?? '—' : '—');

  const { requisicoes, total, totalPaginas, porStatus, isLoading, isFetching, enviarRequisicao, isEnviando } =
    useRequisicaoCompra({
      pagina: pag.pagina,
      tamanhoPagina: pag.tamanhoPagina,
      status: filtros.status === 'todos' ? undefined : filtros.status as StatusRequisicaoCompra,
      filtros: {
        busca:          filtros.busca || undefined,
        ...periodo.intervalo,
        ordenarPor:     ordenacao.ordenarPor,
        decrescente:    ordenacao.decrescente,
        solicitanteId:  filtros.solicitanteId || undefined,
        localEstoqueId: filtros.localEstoqueId || undefined,
      },
    });

  // Contagem do conjunto filtrado (período/busca), calculada no servidor — não só desta página.
  const pendentes  = (porStatus?.Pendente ?? 0) + (porStatus?.EmAprovacao ?? 0);
  const aprovadas  = porStatus?.Aprovada ?? 0;
  const rascunhos  = porStatus?.Rascunho ?? 0;

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Requisições de Compra</h1>
          <p className="text-muted-foreground mt-1">Gerencie solicitações de compra</p>
        </div>
        <Button className="gap-2" onClick={() => navigate('/compras/requisicoes/nova')}>
          <Plus className="h-4 w-4" />Nova Requisição
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <ShoppingBag className="h-4 w-4 text-gray-500" />Rascunhos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{rascunhos}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Send className="h-4 w-4 text-yellow-500" />Pendentes / Em aprovação
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">{pendentes}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-500" />Aprovadas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{aprovadas}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{total}</div>
            <p className="text-xs text-muted-foreground mt-1">No período e filtros selecionados</p>
          </CardContent>
        </Card>
      </div>

      <BarraFiltros
        busca={filtros.busca}
        onBuscar={(busca) => setFiltros({ busca })}
        placeholderBusca="Nº ou justificativa..."
        filtrosAtivos={filtrosAtivos}
        onLimpar={() => { limparFiltros(); periodo.setPeriodo('30dias'); }}
      >
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Status</Label>
          <Select value={filtros.status} onValueChange={(status) => setFiltros({ status })}>
            <SelectTrigger className="h-9 w-44" aria-label="Status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos</SelectItem>
              {Object.entries(statusConfig).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1 w-64">
          <Label className="text-xs text-muted-foreground">Solicitante</Label>
          <UsuarioSearchCombobox
            value={filtros.solicitanteId || undefined}
            onChange={(id) => setFiltros({ solicitanteId: id || 0 })}
            placeholder="Todos os solicitantes"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Local de estoque</Label>
          <Select
            value={filtros.localEstoqueId ? String(filtros.localEstoqueId) : 'todos'}
            onValueChange={(v) => setFiltros({ localEstoqueId: v === 'todos' ? 0 : Number(v) })}>
            <SelectTrigger className="h-9 w-48" aria-label="Local de estoque"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos</SelectItem>
              {locais.map(l => <SelectItem key={l.id} value={String(l.id)}>{l.descricao}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Emissão</Label>
          <div className="flex flex-col sm:flex-row gap-2">
            <PeriodoFiltro estado={periodo} rotuloData="Emissão" />
          </div>
        </div>
        {isFetching && !isLoading && (
          <Loader2 className="h-4 w-4 mb-2.5 animate-spin text-muted-foreground" aria-label="Atualizando" />
        )}
      </BarraFiltros>

      {/* Tabela */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <CabecalhoOrdenavel coluna="numero" ordenacao={ordenacao}>Nº</CabecalhoOrdenavel>
                <CabecalhoOrdenavel coluna="emissao" ordenacao={ordenacao}>Data Emissão</CabecalhoOrdenavel>
                <TableHead>Necessidade</TableHead>
                <TableHead>Solicitante</TableHead>
                <TableHead>Local</TableHead>
                <TableHead>Justificativa</TableHead>
                <CabecalhoOrdenavel coluna="valor" ordenacao={ordenacao} className="text-right">Valor Total</CabecalhoOrdenavel>
                <TableHead>Status</TableHead>
                <TableHead className="w-24">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">Carregando...</TableCell>
                </TableRow>
              ) : requisicoes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                    {periodo.periodo !== 'todos'
                      ? 'Nenhuma requisição neste período — amplie o período para ver as anteriores'
                      : 'Nenhuma requisição encontrada'}
                  </TableCell>
                </TableRow>
              ) : (
                requisicoes.map(r => {
                  const cfg = statusConfig[r.status] ?? { label: String(r.status), className: '' };
                  return (
                    <TableRow key={r.id}>
                      <TableCell className="font-mono font-bold">#{r.numero}</TableCell>
                      <TableCell>{new Date(r.dtEmissao).toLocaleDateString('pt-BR')}</TableCell>
                      <TableCell>
                        {r.dtNecessidade
                          ? new Date(r.dtNecessidade).toLocaleDateString('pt-BR')
                          : '—'}
                      </TableCell>
                      <TableCell><UsuarioNome id={r.usuarioSolicitanteId} /></TableCell>
                      <TableCell>{nomeLocal(r.localEstoqueId)}</TableCell>
                      <TableCell className="max-w-48 truncate">{r.justificativa ?? '—'}</TableCell>
                      <TableCell className="text-right font-mono">
                        {r.valorTotal.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                      </TableCell>
                      <TableCell>
                        <Badge className={cfg.className}>{cfg.label}</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" title="Ver detalhes"
                            onClick={() => navigate(`/compras/requisicoes/${r.id}`)}>
                            <Eye className="h-4 w-4" />
                          </Button>
                          {r.status === 'Rascunho' && (
                            <Button size="sm" variant="ghost" title="Enviar para aprovação"
                              disabled={isEnviando}
                              onClick={() => enviarRequisicao(r.id)}>
                              <Send className="h-4 w-4 text-blue-500" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <PaginacaoRodape {...pag.rodape({ total, totalPaginas })} />
    </div>
  );
};

export default RequisicaoCompra;
